import type { MainSidebarState } from '@neo/blocks/sidebar/app-sidebar'
import type { ReportSection } from '@neo/blocks/sidebar/app-sidebar-reports'
import { SheetDescription, SheetTitle } from '@neo/primitives/sheet'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@neo/primitives/sidebar'
import { ReactComponent as PlivoIcon } from '@shared/assets/icons/ic-plivo.svg'
import { useState } from 'react'
import { Link, useLocation } from 'router-v6'
import { getDefaultRoute } from 'v2/configs/routes'
import {
  isLinkActive,
  isResolvedGroup,
  type ResolvedNavLink,
  type ResolvedWorkspace,
} from 'v2/shared/navigation/workspace-nav-utils'
import type { IRoute } from 'v2/types'
import { SidebarToggle } from './sidebar-toggle'
import { useWorkspace } from './use-workspace'
import { useWorkspaceNav } from './use-workspace-nav'
import { NavGroupItem, NavLinkItem, useCloseMobileSidebar } from './workspace-nav-items'
import { WorkspaceSwitcher } from './workspace-switcher'

const activeGroupKey = (workspace: ResolvedWorkspace, pathname: string) =>
  workspace.sections
    .flatMap((section) => section.entries)
    .find((entry) => isResolvedGroup(entry) && entry.items.some((i) => isLinkActive(pathname, i)))
    ?.key

interface WorkspaceNavBodyProps {
  workspace: ResolvedWorkspace
  home?: ResolvedNavLink
}

export const WorkspaceNavBody = ({ workspace, home }: WorkspaceNavBodyProps) => {
  const { pathname } = useLocation()
  const activeKey = activeGroupKey(workspace, pathname)
  // One group open at a time, following the page: a manual choice holds only while the page's
  // group is the one it was made on, so a new page opens its own group in the same render.
  const [choice, setChoice] = useState({ forActive: activeKey, key: activeKey })
  const expandedKey = choice.forActive === activeKey ? choice.key : activeKey
  const setExpandedKey = (key: string | undefined) => setChoice({ forActive: activeKey, key })

  return (
    <>
      {home && (
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <NavLinkItem link={home} exact />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}
      {workspace.sections.map((section) => (
        <SidebarGroup key={section.label}>
          <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {section.entries.map((entry) =>
                isResolvedGroup(entry) ? (
                  <NavGroupItem
                    key={entry.key}
                    group={entry}
                    expanded={expandedKey === entry.key}
                    onExpandedChange={(expanded) =>
                      setExpandedKey(expanded ? entry.key : undefined)
                    }
                  />
                ) : (
                  <NavLinkItem key={entry.key} link={entry} />
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  )
}

const SidebarLogo = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const closeMobile = useCloseMobileSidebar()
  return (
    <Link
      to={getDefaultRoute()}
      aria-label="Plivo Home"
      onClick={closeMobile}
      className="flex w-fit items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="sidebar-logo-btn"
    >
      {isCollapsed ? (
        <PlivoIcon className="size-6 dark:invert" aria-hidden />
      ) : (
        <img src="/plivo-logo.svg" className="h-4 dark:invert" alt="Plivo" />
      )}
    </Link>
  )
}

const SupportFooter = ({ link }: { link: ResolvedNavLink }) => {
  const { pathname } = useLocation()
  const closeMobile = useCloseMobileSidebar()
  return (
    <SidebarFooter className="border-t border-sidebar-border">
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            tooltip={link.title}
            isActive={isLinkActive(pathname, link)}
            asChild
            className="border border-sidebar-border bg-background shadow-sm hover:bg-hv1"
          >
            <Link to={link.path} onClick={closeMobile} className="flex w-full items-center gap-2">
              {link.icon && <link.icon className="size-3.5 text-primary" />}
              <span className="flex-1 font-medium">{link.title}</span>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  )
}

interface WorkspaceSidebarProps extends MainSidebarState {
  routes: IRoute[]
  reportSections: ReportSection[]
}

export const WorkspaceSidebar = ({
  isCollapsible,
  disableTransition,
  routes,
  reportSections,
}: WorkspaceSidebarProps) => {
  const nav = useWorkspaceNav(routes, reportSections)
  const { current, selectWorkspace } = useWorkspace(nav.workspaces.map((w) => w.id))
  const { state, isMobile } = useSidebar()
  const isCollapsed = state === 'collapsed' && !isMobile
  const workspace = nav.workspaces.find((w) => w.id === current)

  return (
    <Sidebar
      collapsible={isCollapsible ? 'icon' : 'none'}
      disableTransition={disableTransition}
      className="group"
      data-testid="sidebar-workspace-navigation"
    >
      {/* On mobile the sidebar is a dialog sheet, which needs a name for screen readers. */}
      {isMobile && isCollapsible && (
        <>
          <SheetTitle className="sr-only">Platform navigation</SheetTitle>
          <SheetDescription className="sr-only">
            Switch products, choose a page, or expand a category.
          </SheetDescription>
        </>
      )}
      <SidebarHeader className="h-14 shrink-0 flex-row items-center justify-between pl-6 pr-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:pt-2">
        <SidebarLogo isCollapsed={isCollapsed} />
        {!isCollapsed && isCollapsible && <SidebarToggle />}
      </SidebarHeader>
      {workspace && (
        <WorkspaceSwitcher
          workspaces={nav.workspaces}
          current={workspace}
          onSelect={selectWorkspace}
        />
      )}
      <SidebarContent className="[&_[data-sidebar=group]]:px-3 [&_[data-sidebar=group-content]]:px-0 group-data-[collapsible=icon]:[&_[data-sidebar=group]]:px-2">
        {workspace && <WorkspaceNavBody key={workspace.id} workspace={workspace} home={nav.home} />}
      </SidebarContent>
      {nav.support && <SupportFooter link={nav.support} />}
    </Sidebar>
  )
}
